import React, { useEffect, useState } from 'react';
import Sidebar from '../components/layout/sidebar';
import Topbar from '../components/layout/Topbar';
import { supabase } from '../supabase';
import './supportMessages.css';

const SupportMessages = () => {
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');

  useEffect(() => {
    if (localStorage.getItem('isAdminLoggedIn') !== 'true') {
      window.location.href = '/';
      return;
    }

    fetchMessages();
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [search, statusFilter, priorityFilter]);

  const fetchMessages = async () => {
    let query = supabase
      .from('support_messages')
      .select(`
        *,
        support_feedback (
          rating,
          comment
        )
      `)
      .order('created_at', { ascending: false });

    if (search) {
      query = query.or(
        `name.ilike.%${search}%,email.ilike.%${search}%,subject.ilike.%${search}%,message.ilike.%${search}%`
      );
    }

    if (statusFilter) {
      query = query.eq('status', statusFilter);
    }

    if (priorityFilter) {
      query = query.eq('priority', priorityFilter);
    }

    const { data, error } = await query;

    if (error) {
      alert(error.message);
      return;
    }

    setMessages(data || []);
  };

  const markResolved = async (id) => {
    const { error } = await supabase
      .from('support_messages')
      .update({ status: 'resolved' })
      .eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    fetchMessages();
  };

  const deleteMessage = async (id) => {
    const confirmDelete = window.confirm(
      'Are you sure you want to delete this support message?'
    );

    if (!confirmDelete) return;

    const { error } = await supabase.from('support_messages').delete().eq('id', id);

    if (error) {
      alert(error.message);
      return;
    }

    setSelectedMessage(null);
    fetchMessages();
  };

  const getRating = (message) => {
    const feedback = message.support_feedback?.[0];
    return feedback?.rating || null;
  };

  const ratings = messages
    .map((message) => getRating(message))
    .filter((rating) => rating !== null);

  const avgRating =
    ratings.length > 0
      ? (ratings.reduce((sum, rating) => sum + Number(rating), 0) / ratings.length).toFixed(1)
      : '0.0';

  return (
    <div className="dashboard-page">
      <Sidebar active="Support Messages" />

      <main className="dashboard-main">
        <Topbar />

        <section className="support-content">
          <div className="support-header">
            <h1>Support Messages</h1>
            <p>Manage customer inquiries and support tickets</p>
          </div>

          <div className="support-stats-grid">
            <div className="support-stat-card">
              <p>Total Messages</p>
              <h2>{messages.length}</h2>
            </div>

            <div className="support-stat-card">
              <p>Pending</p>
              <h2 className="orange">
                {messages.filter((message) => message.status === 'pending').length}
              </h2>
            </div>

            <div className="support-stat-card">
              <p>Resolved</p>
              <h2 className="green">
                {messages.filter((message) => message.status === 'resolved').length}
              </h2>
            </div>

            <div className="support-stat-card">
              <p>Avg Rating</p>
              <h2 className="pink">{avgRating}</h2>
            </div>
          </div>

          <div className="support-filter-card">
            <div className="support-search">
              <span>⌕</span>
              <input
                placeholder="Search messages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="resolved">Resolved</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="">All Priority</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="support-layout">
            <div className="support-list">
              {messages.map((message) => (
                <button
                  key={message.id}
                  className={`support-card ${
                    selectedMessage?.id === message.id ? 'selected' : ''
                  }`}
                  onClick={() => setSelectedMessage(message)}
                >
                  <div className="support-card-top">
                    <div>
                      <h3>♙ {message.name}</h3>
                      <p>{message.email}</p>
                    </div>

                    <div className="support-badges">
                      <span className={`priority ${message.priority || 'medium'}`}>
                        {message.priority || 'medium'}
                      </span>
                      <span className={`message-status ${message.status}`}>
                        {message.status}
                      </span>
                    </div>
                  </div>

                  <h4>{message.subject}</h4>
                  <p className="message-preview">{message.message}</p>

                  <div className="support-card-bottom">
                    <span>
                      ◷ {new Date(message.created_at).toISOString().slice(0, 10)}
                    </span>
                    <span>{message.category || 'General'}</span>
                  </div>

                  {getRating(message) && (
                    <p className="support-rating">
                      Rating: {'★'.repeat(getRating(message))}
                    </p>
                  )}
                </button>
              ))}

              {messages.length === 0 && (
                <div className="empty-support">No messages found.</div>
              )}
            </div>

            <div className="support-details">
              {!selectedMessage ? (
                <div className="no-message-selected">
                  <div>▢</div>
                  <p>Select a message to view details</p>
                </div>
              ) : (
                <div className="message-details-card">
                  <div className="message-details-header">
                    <div>
                      <h2>{selectedMessage.subject}</h2>
                      <p>{selectedMessage.name} — {selectedMessage.email}</p>
                    </div>

                    <span className={`message-status ${selectedMessage.status}`}>
                      {selectedMessage.status}
                    </span>
                  </div>

                  <p className="details-message">{selectedMessage.message}</p>

                  <div className="details-info">
                    <p><strong>Date:</strong> {new Date(selectedMessage.created_at).toISOString().slice(0, 10)}</p>
                    <p><strong>Category:</strong> {selectedMessage.category || 'General'}</p>
                    <p><strong>Priority:</strong> {selectedMessage.priority || 'medium'}</p>
                  </div>

                  {getRating(selectedMessage) && (
                    <div className="details-rating">
                      <strong>Support Rating:</strong>
                      <span>{'★'.repeat(getRating(selectedMessage))}</span>
                    </div>
                  )}

                  <div className="details-actions">
                    {selectedMessage.status !== 'resolved' && (
                      <button onClick={() => markResolved(selectedMessage.id)}>
                        Mark as Resolved
                      </button>
                    )}

                    <button onClick={() => deleteMessage(selectedMessage.id)}>
                      Delete Message
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        <footer className="dashboard-footer">
          <p>© 2026 Spine Literary Events Platform. All rights reserved.</p>
          <p>Admin Dashboard v1.0</p>
        </footer>
      </main>
    </div>
  );
};

export default SupportMessages;